import type WaButton from "@web.awesome.me/webawesome-pro/dist/components/button/button.js";
import type WaInput from "@web.awesome.me/webawesome-pro/dist/components/input/input.js";
import htmx from "htmx.org";
import { html } from "lit";
import { checkNotNil } from "../check-not-nil.js";
import { type FormSubmissionHandler, formSubmissionHandler } from "../form.js";
import { AuthenticationError } from "../schemas/authentication-error.js";
import { formStyles } from "../styles/form.styles.js";
import { hostStyles } from "../styles/host.styles.js";
import { WmElement } from "./element.js";

export class WmSignIn extends WmElement {
  static readonly TAG_NAME = "wm-sign-in";

  static styles = [hostStyles, formStyles];

  #formSubmit: FormSubmissionHandler;

  constructor() {
    super();
    this.#formSubmit = formSubmissionHandler("/auth/sign-in", {
      method: "POST",
    })
      .on("pre:response", () => (this.#querySubmitButton().loading = true))
      .on("response", (r) => this.#handleResponse(r))
      .on("pre:response", () => (this.#querySubmitButton().loading = false))
      .on("error", (err) =>
        this.logger.error("unable to POST /auth/sign-in", err),
      );
  }

  protected override render(): unknown {
    return html`
      <form @submit=${{ handleEvent: this.#formSubmit }}>
        <wa-input
          label="Email"
          name="email"
          type="email"
          autocomplete="on"
          required
          @input=${this.#handleEmailInput}>
        </wa-input>
        <wa-input
          label="Password"
          name="password"
          type="password"
          autocomplete="on"
          password-toggle
          required
          @input=${this.#handlePasswordInput}>
        </wa-input>
        <wa-button variant="success" type="submit">Sign In</wa-button>
      </form>
    `;
  }

  #handleEmailInput(e: InputEvent) {
    e.stopPropagation();
    const target = checkNotNil(e.target) as WaInput;
    target.setCustomValidity("");
  }

  #handlePasswordInput(e: InputEvent) {
    e.stopPropagation();
    // Unlike most form controls, only the email input reports errors, and for
    // password errors as well. this means that new password input should reset
    // the validity on *the email* control.
    const emailInput = this.#queryEmailInput();
    emailInput.setCustomValidity("");
  }

  async #handleResponse(response: Response) {
    if (response.status === 401) {
      const validationError = AuthenticationError.parse(await response.json());
      const reason = validationError.error;
      // The error is always associated with the email, even though the
      // password might be at fault. This is so we won't tell a miscreant
      // that we know about a user's email.
      const input = this.#queryEmailInput();
      input.setCustomValidity(reason);
      input.reportValidity();
    } else {
      return htmx.ajax("GET", "/app", { push: "true" });
    }
  }

  #queryEmailInput(): WaInput {
    return this.queryNotNil(`wa-input[name="email"]`);
  }

  #querySubmitButton(): WaButton {
    return this.queryNotNil(`wa-button[type="submit"]`);
  }
}

customElements.define(WmSignIn.TAG_NAME, WmSignIn);

declare global {
  interface HTMLElementTagNameMap {
    [WmSignIn.TAG_NAME]: WmSignIn;
  }
}
